import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Home, Mail, FileText, Calendar, User, MapPin, Phone, Send, Package, ReceiptPoundSterling, BadgeCheck, ShieldCheck, Truck, CheckCircle2, Clock } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import OrderStageTimeline from '@/components/OrderStageTimeline';
import OrderStageActionModal from '@/components/OrderStageActionModal';
import OrderDeleteButton from '@/components/OrderDeleteButton';
import { formatApiError } from '@/utils/apiError';
import { InvoiceExpiredNotice } from '@/components/orders/InvoiceExpiredNotice';

const PAYMENT_METHODS = [
  { key: 'go_cardless',   label: 'GoCardless' },
  { key: 'bank_transfer', label: 'Bank Transfer' },
  { key: 'card',          label: 'Card' },
  { key: 'cash',          label: 'Cash' },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const STATUS_COLOURS = {
  draft:             'bg-sky-100 text-sky-800',
  submitted: 'bg-amber-100 text-amber-800',
  pending: 'bg-amber-100 text-amber-800',
  invoiced: 'bg-indigo-100 text-indigo-800',
  paid: 'bg-teal-100 text-teal-800',
  payment_confirmed: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-800',
};

export default function ProBoundOrderDetail() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { isStaff } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [stageAction, setStageAction] = useState(null);     // 'invoiced' | 'paid' | 'payment_confirmed' | 'dispatched' | 'delivered' | null
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [expectedEta, setExpectedEta] = useState('');
  const [submittingTransition, setSubmittingTransition] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes/${orderId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed to load order');
      setOrder(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { (async () => { await load(); })(); }, [load]);

  const resendEmail = async () => {
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes/${orderId}/resend-email`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Resend failed');
      if (data.sent) {
        toast.success(`Order email re-sent for ${data.reference} (office + installer)`);
      } else {
        toast.error('Resend reported a failure — check Resend dashboard logs');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResending(false);
    }
  };

  const activeAncillaries = useMemo(
    () => Object.entries(order?.ancillaries || {}).filter(([, v]) => v > 0),
    [order?.ancillaries],
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading order…</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center text-gray-500">Order not found.</div>;

  const status = order.status || 'submitted';
  const closeStageModal = () => {
    if (submittingTransition) return;
    setStageAction(null);
    setInvoiceNumber('');
    setPaymentMethod('');
    setExpectedEta('');
  };

  const submitTransition = async (targetStatus, extras = {}) => {
    setSubmittingTransition(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes/${orderId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetStatus, ...extras }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Update failed');
      }
      toast.success(`Order marked as ${targetStatus.replace('_', ' ')}`);
      closeStageModal();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingTransition(false);
    }
  };

  const placeOrder = async () => {
    if (!window.confirm('Place a confirmed order with Ox Resin for these materials?')) return;
    setSubmittingTransition(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes/${orderId}/place-order`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Could not place the order');
      toast.success(`Order ${body.reference} placed with Ox Resin`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setSubmittingTransition(false); }
  };

  const stageActionButtons = (() => {
    const actions = {};
    // Stage 1 — Draft material price (from a customer quote): installer or staff can place the order
    if (status === 'draft') {
      actions.order = (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" data-testid="pbod-place-order-btn" onClick={placeOrder} disabled={submittingTransition}>
          <Package className="w-3.5 h-3.5 mr-1" /> Order these materials
        </Button>
      );
    }
    // Stage 2 — Invoiced (staff only, from submitted)
    if (isStaff && status === 'submitted') {
      actions.raise_invoice = (
        <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-800 whitespace-nowrap" data-testid="pbod-raise-invoice-btn" onClick={() => navigate(`/staff/invoices?from=probound&order=${order.id}`)}>
          <FileText className="w-3.5 h-3.5 mr-1" /> Raise invoice in Xero
        </Button>
      );
      actions.invoiced = (
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap" data-testid="pbod-mark-invoiced-btn" onClick={() => setStageAction('invoiced')}>
          <ReceiptPoundSterling className="w-3.5 h-3.5 mr-1" /> Mark as invoiced
        </Button>
      );
    }
    // Stage 3 — Customer Paid (installer only, from invoiced)
    if (!isStaff && status === 'invoiced') {
      actions.paid = (
        <Button size="sm" className={order.confirmed_total != null ? "bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap" : "bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 whitespace-nowrap"} data-testid="pbod-mark-paid-btn" onClick={() => setStageAction('paid')}>
          {order.confirmed_total != null ? <><BadgeCheck className="w-3.5 h-3.5 mr-1" /> I've paid — mark as paid</> : <><Clock className="w-3.5 h-3.5 mr-1" /> Awaiting confirmed amount</>}
        </Button>
      );
    }
    // Stage 4 — Payment Confirmed (staff only, from paid)
    if (isStaff && status === 'paid') {
      actions.payment_confirmed = (
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap" data-testid="pbod-mark-payment-confirmed-btn" onClick={() => setStageAction('payment_confirmed')}>
          <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Mark as payment confirmed
        </Button>
      );
    }
    // Stage 5 — Dispatched (staff only, from payment_confirmed or paid)
    if (isStaff && (status === 'payment_confirmed' || status === 'paid')) {
      actions.dispatched = (
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap" data-testid="pbod-mark-dispatched-btn" onClick={() => setStageAction('dispatched')}>
          <Truck className="w-3.5 h-3.5 mr-1" /> Mark as dispatched
        </Button>
      );
    }
    // Stage 6 — Delivered (installer only, from dispatched)
    if (!isStaff && status === 'dispatched') {
      actions.delivered = (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" data-testid="pbod-mark-delivered-btn" onClick={() => setStageAction('delivered')}>
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as delivered
        </Button>
      );
    }
    return actions;
  })();

  const calc = order.calculation || {};
  const snap = order.pricing_snapshot || null;
  const total = snap?.total_inc_vat ?? calc.total_cost ?? calc.totalCost ?? order.total ?? 0;
  const lineItems = calc.line_items || calc.lineItems || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #2F5E96 75%)', borderBottom: '3px solid #2F5E96' }}>
        <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back" data-testid="pbod-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <Package className="w-5 h-5 sm:w-6 sm:h-6" /> ProBound Order
            </h1>
            <p className="text-white/70 text-xs sm:text-sm mt-0.5"><code className="font-bold">{order.reference}</code> · submitted {new Date(order.created_at).toLocaleString('en-GB')}</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="pbod-home">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Staff</span>
          </Button>
          <OrderDeleteButton order={order} product="probound" />
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-5 space-y-4">
        {/* Status + actions */}
        <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${STATUS_COLOURS[order.status] || 'bg-gray-100 text-gray-700'}`}>
              {order.status || 'submitted'}
            </span>
            <span className="text-sm text-gray-500">Priority: <strong className="text-gray-800">{order.priority || 'no_rush'}</strong></span>
            {order.confirmed_total != null
              ? <span className="text-sm text-emerald-700" data-testid="pbod-total-confirmed">Confirmed by Ox Resin: <strong>{fmtGBP(order.confirmed_total)}</strong></span>
              : <span className="text-sm text-amber-700" data-testid="pbod-total-estimate">Estimate: <strong>{fmtGBP(total)}</strong> · to be confirmed</span>}
            {order.xero_invoice_id && (
              <a
                href={order.xero_invoice_url || '#'}
                target={order.xero_invoice_url ? '_blank' : undefined}
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 no-underline"
                data-testid="pbod-xero-invoice-chip"
                title="Open the draft invoice in Xero"
              >
                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><circle cx="12" cy="12" r="12" opacity="0.15"/><text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight="700" fontFamily="sans-serif">X</text></svg>
                Xero {order.xero_invoice_status || 'DRAFT'}{order.xero_invoice_number ? ` · ${order.xero_invoice_number}` : ''}
              </a>
            )}
            {!order.xero_invoice_id && order.xero_last_error && isStaff && (
              <span
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200"
                data-testid="pbod-xero-error-chip"
                title={order.xero_last_error}
              >
                Xero push failed
              </span>
            )}
          </div>
          {isStaff && (
            <Button onClick={resendEmail} disabled={resending} variant="outline" size="sm" data-testid="pbod-resend-email">
              <Send className="w-4 h-4 mr-1.5" /> {resending ? 'Sending…' : 'Resend order email'}
            </Button>
          )}
        </Card>

        {/* Stage Timeline */}
        <InvoiceExpiredNotice order={order} product="probound" isStaff={isStaff} onReactivated={load} />
        <Card className="p-4" data-testid="pbod-stage-timeline-card">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Order Stages</h2>
          <OrderStageTimeline order={order} actions={stageActionButtons} />
        </Card>

        {/* Customer */}
        <Card className="p-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Customer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /> {order.customer_details?.name}</div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {order.customer_details?.email}</div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {order.customer_details?.phone || '—'}</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /> {order.customer_details?.address || '—'}</div>
            {order.customer_details?.start_date && (
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" /> Start: {order.customer_details.start_date}</div>
            )}
          </div>
        </Card>

        {/* Blend Areas */}
        {(order.blend_areas || []).length > 0 && (
          <Card className="p-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Blend Areas</h2>
            <div className="space-y-2">
              {order.blend_areas.map((a, i) => {
                const unit = order.measurement_type === 'Mixes' ? 'mixes' : 'm²';
                return (
                  <div key={`${a.surface_blend || 'blend'}-${i}`} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="font-semibold">Area {i + 1} · {a.surface_blend}</div>
                    <div className="text-gray-600 mt-1">
                      <strong>{a.quantity} {unit}</strong>
                      {a.specification ? ` · ${a.specification}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Priced Breakdown — frozen at submit time from the installer's live cost feed.
            This is the source of truth for the £ that will land on the Xero draft invoice. */}
        {snap && (snap.lines?.length > 0 || snap.subtotal_ex_vat != null) && (
          <Card className="p-4" data-testid="pbod-pricing-snapshot">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500">Priced Breakdown</h2>
              {snap.tier_name && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                  {snap.tier_name} tier
                </span>
              )}
            </div>
            {snap.lines?.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="pb-1">Item</th>
                    <th className="pb-1 text-right">Qty</th>
                    <th className="pb-1 text-right">Unit price</th>
                    <th className="pb-1 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.lines.map((l, i) => (
                    <tr key={`${l.label}-${i}`} className="border-t border-gray-100">
                      <td className="py-1.5">{l.label}</td>
                      <td className="py-1.5 text-right">{l.qty}{l.unit_label ? ` ${l.unit_label}` : ''}</td>
                      <td className="py-1.5 text-right">{fmtGBP(l.unit_price)}</td>
                      <td className="py-1.5 text-right font-semibold">{fmtGBP(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {snap.missing_price_labels?.length > 0 && (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                No live price captured for: {snap.missing_price_labels.join(', ')}
              </div>
            )}
            <div className="border-t pt-3 mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal (ex VAT)</span><span className="font-semibold">{fmtGBP(snap.subtotal_ex_vat)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">VAT ({Math.round((snap.vat_rate || 0.2) * 100)}%)</span><span className="font-semibold">{fmtGBP(snap.vat_amount)}</span></div>
              <div className="flex justify-between items-baseline pt-1"><span className="text-sm text-gray-500">Total (inc VAT)</span><span className="text-2xl font-bold text-gray-900">{fmtGBP(snap.total_inc_vat)}</span></div>
            </div>
          </Card>
        )}

        {/* Calculation */}
        <Card className="p-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Calculation</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
            <Stat label="Total m²" value={calc.total_m2} />
            <Stat label="Mixes before wastage" value={calc.total_quantity_before_wastage} />
            <Stat label="Mixes after wastage" value={calc.total_quantity_after_wastage} />
            <Stat label="Mesh rolls" value={calc.mesh_rolls_required} />
          </div>
          {lineItems.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <h3 className="text-xs font-semibold text-gray-600 mb-2">Line items</h3>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500"><th className="pb-1">Item</th><th className="pb-1 text-right">Qty</th><th className="pb-1 text-right">Price</th><th className="pb-1 text-right">Total</th></tr></thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={`${li.name || li.label || 'line'}-${i}`} className="border-t border-gray-100">
                      <td className="py-1.5">{li.name || li.label}</td>
                      <td className="py-1.5 text-right">{li.qty ?? li.quantity ?? '—'}</td>
                      <td className="py-1.5 text-right">{fmtGBP(li.price ?? li.unit_price)}</td>
                      <td className="py-1.5 text-right font-semibold">{fmtGBP(li.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t pt-3 mt-3 flex justify-end items-baseline gap-2">
            <span className={`text-sm ${order.confirmed_total != null ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}`}>{order.confirmed_total != null ? 'Confirmed by Ox Resin' : 'Estimate'}</span>
            <span className={`text-2xl font-bold ${order.confirmed_total != null ? 'text-emerald-800' : 'text-gray-500'}`}>{fmtGBP(order.confirmed_total ?? total)}</span>
          </div>
          {order.confirmed_total == null && !isStaff && ['submitted', 'invoiced', 'pending'].includes(order.status) && (
            <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pbod-dont-pay-notice"><strong>Please don't pay yet.</strong> This is the app's estimate — the Ox Resin office will confirm the exact amount to pay and you'll see it here as <em>Confirmed</em>.</p>
          )}
        </Card>

        {/* Ancillaries */}
        {activeAncillaries.length > 0 && (
          <Card className="p-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Ancillaries</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              {activeAncillaries.map(([k, v]) => (
                <div key={k} className="p-2 bg-gray-50 rounded">{k.replace(/_/g, ' ')}: <strong>{v}</strong></div>
              ))}
            </div>
          </Card>
        )}

        {/* Border */}
        {order.border && (
          <Card className="p-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Resin Border</h2>
            <div className="text-sm text-gray-700 space-y-1">
              <div>Blend colour: <strong>{order.border.blend_color}</strong></div>
              {order.border.length && order.border.width && <div>Dimensions: {order.border.length}m × {order.border.width}mm</div>}
              {order.border.mixes > 0 && <div>Mixes: {order.border.mixes}</div>}
            </div>
          </Card>
        )}

        {/* Raw JSON dump for debugging — collapsed */}
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700">Show raw payload (debug)</summary>
          <pre className="mt-2 p-3 bg-gray-100 rounded overflow-auto whitespace-pre-wrap break-words text-[11px]">{JSON.stringify(order, null, 2)}</pre>
        </details>
      </div>

      {/* Stage transition modals */}
      <OrderStageActionModal
        action={stageAction}
        order={order}
        invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
        paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
        expectedEta={expectedEta} setExpectedEta={setExpectedEta}
        submitting={submittingTransition}
        isStaff={isStaff}
        onClose={closeStageModal}
        onConfirm={(extra = {}) => {
          if (stageAction === 'invoiced') submitTransition('invoiced', { invoice_number: invoiceNumber.trim(), ...extra });
          else if (stageAction === 'payment_confirmed') submitTransition('payment_confirmed', { payment_method: paymentMethod });
          else if (stageAction === 'dispatched') submitTransition('dispatched', expectedEta ? { expected_delivery_date: expectedEta } : {});
          else submitTransition(stageAction, extra);
        }}
      />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="p-2 bg-gray-50 rounded">
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-base font-semibold text-gray-900">{value ?? '—'}</div>
  </div>
);
